import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, FileText, Clock, CheckCircle, User, Download,
  ThumbsUp, ThumbsDown, Send, Loader2, AlertCircle, Sparkles, Search, ChevronDown
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { AuditTrailViewer } from '../../components/AuditTrail';
import { useAlert } from '../../context/AlertContext';

const DocumentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError, confirm } = useAlert();
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [reviewerId, setReviewerId] = useState('');
  const [reviewComments, setReviewComments] = useState('');
  const [acknowledgments, setAcknowledgments] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [aiSummary, setAiSummary] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [reviewerSearch, setReviewerSearch] = useState('');
  const [showReviewerDropdown, setShowReviewerDropdown] = useState(false);
  const [selectedReviewerName, setSelectedReviewerName] = useState('');
  const reviewerDropdownRef = useRef(null);
  const [activeTab, setActiveTab] = useState('details'); // 'details' or 'audit'

  useEffect(() => {
    fetchDocument();
    fetchUsers();
    fetchAcknowledgments();
    const userData = JSON.parse(localStorage.getItem('user'));
    setCurrentUser(userData);
  }, [id]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (reviewerDropdownRef.current && !reviewerDropdownRef.current.contains(event.target)) {
        setShowReviewerDropdown(false);
      }
    };
    window.document.addEventListener('mousedown', handleClickOutside);
    return () => window.document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchDocument = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/documents/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDocument(response.data);
    } catch (error) {
      console.error('Error fetching document:', error);
      showError('Document not found');
      navigate('/documents');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/users`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchAcknowledgments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/documents/${id}/acknowledgments`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAcknowledgments(response.data);
    } catch (error) {
      console.error('Error fetching acknowledgments:', error);
    }
  };

  const handleSubmitForReview = async () => {
    if (!reviewerId) {
      showError('Please select a reviewer');
      return;
    }

    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/documents/${id}/submit-for-review?reviewer_id=${reviewerId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess('Document submitted for review');
      fetchDocument();
    } catch (error) {
      console.error('Error submitting for review:', error);
      showError(error.response?.data?.detail || 'Failed to submit for review');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReview = async (action) => {
    if (!reviewComments && action !== 'approve') {
      showError('Please provide comments');
      return;
    }

    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/documents/${id}/review`,
        {
          action: action,
          comments: reviewComments
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess(`Document ${action}d successfully`);
      setReviewComments('');
      fetchDocument();
    } catch (error) {
      console.error('Error reviewing document:', error);
      showError(error.response?.data?.detail || 'Failed to review document');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePublish = async () => {
    const confirmed = await confirm(
      'Publish Document',
      'Are you sure you want to publish this document? It will be visible to all users.',
      { destructive: false, confirmLabel: 'Publish' }
    );
    if (!confirmed) return;

    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/documents/${id}/publish`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess('Document published successfully');
      fetchDocument();
    } catch (error) {
      console.error('Error publishing document:', error);
      showError(error.response?.data?.detail || 'Failed to publish document');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAcknowledge = async () => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/documents/${id}/acknowledge`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess('Document acknowledged successfully');
      fetchAcknowledgments();
    } catch (error) {
      console.error('Error acknowledging document:', error);
      showError(error.response?.data?.detail || 'Failed to acknowledge document');
    } finally {
      setActionLoading(false);
    }
  };

  const handleGenerateAISummary = async () => {
    setAiLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/documents/${id}/ai-summary`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAiSummary(response.data.summary);
      showSuccess('AI Summary generated successfully!');
    } catch (error) {
      console.error('Error generating AI summary:', error);
      showError(error.response?.data?.detail || 'Failed to generate AI summary');
    } finally {
      setAiLoading(false);
    }
  };

  const handleDownloadVersion = async (version) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/documents/${id}/download/${version}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link using window.document to avoid naming conflict
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const linkElement = window.document.createElement('a');
      linkElement.href = url;
      linkElement.setAttribute('download', `${document.title}_${version}.pdf`);
      window.document.body.appendChild(linkElement);
      linkElement.click();
      linkElement.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      showError(error.response?.data?.detail || 'Failed to download document');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'Draft': 'bg-gray-100 text-gray-700',
      'Under_Review': 'bg-yellow-100 text-yellow-700',
      'Approved': 'bg-green-100 text-green-700',
      'Published': 'bg-blue-100 text-blue-700',
      'Archived': 'bg-red-100 text-red-700'
    };
    return badges[status] || 'bg-gray-100 text-gray-700';
  };

  const canReview = () => {
    const userId = currentUser?.user_id || currentUser?.id;
    return document?.current_status === 'Under_Review' && 
           document?.current_reviewer_id === userId;
  };

  const needsAcknowledgment = () => {
    if (!document?.mandatory_reading || document?.current_status !== 'Published') return false;
    if (!acknowledgments) return false;
    
    const userId = currentUser?.user_id || currentUser?.id;
    const hasAcknowledged = acknowledgments.acknowledgments.some(
      ack => ack.user_id === userId && ack.version_acknowledged === document.current_version
    );
    return !hasAcknowledged;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!document) return null;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => navigate('/documents')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Documents
          </Button>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <FileText className="w-8 h-8 text-blue-600" />
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900">{document.title}</h1>
                    <p className="text-sm text-gray-500 mt-1">{document.document_number}</p>
                  </div>
                </div>
                <p className="text-gray-600 mt-2">{document.description}</p>
              </div>
              <span className={`px-4 py-2 rounded-full text-sm font-semibold ${getStatusBadge(document.current_status)}`}>
                {document.current_status.replace('_', ' ')}
              </span>
            </div>

            {/* Metadata Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t">
              <div>
                <p className="text-sm text-gray-500">Version</p>
                <p className="font-semibold text-gray-900">{document.current_version}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Type</p>
                <p className="font-semibold text-gray-900">{document.document_type.replace('_', ' ')}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Department</p>
                <p className="font-semibold text-gray-900">{document.department}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Author</p>
                <p className="font-semibold text-gray-900">{document.author_name}</p>
              </div>
            </div>

            {document.effective_date && (
              <div className="mt-4 pt-4 border-t">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Effective Date</p>
                    <p className="font-semibold text-gray-900">
                      {new Date(document.effective_date).toLocaleDateString()}
                    </p>
                  </div>
                  {document.next_review_due && (
                    <div>
                      <p className="text-sm text-gray-500">Next Review Due</p>
                      <p className="font-semibold text-gray-900">
                        {new Date(document.next_review_due).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Acknowledgment Alert */}
        {needsAcknowledgment() && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-yellow-600 mr-3" />
                <p className="text-yellow-800 font-semibold">
                  This document requires your acknowledgment
                </p>
              </div>
              <Button
                onClick={handleAcknowledge}
                disabled={actionLoading}
                className="bg-yellow-600 hover:bg-yellow-700"
              >
                {actionLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Acknowledge Reading'
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('details')}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'details'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Details
            </button>
            <button
              onClick={() => setActiveTab('audit')}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'audit'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Audit History
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'details' ? (
          <>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Document Content */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Document Content</h2>
                <Button
                  onClick={handleGenerateAISummary}
                  disabled={aiLoading}
                  variant="outline"
                  className="text-purple-600"
                >
                  {aiLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  Generate AI Summary
                </Button>
              </div>

              {/* AI Summary */}
              {aiSummary && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                  <h3 className="font-semibold text-purple-900 mb-2 flex items-center">
                    <Sparkles className="w-4 h-4 mr-2" />
                    AI-Generated Summary
                  </h3>
                  <ul className="list-disc list-inside space-y-1">
                    {aiSummary.map((point, idx) => (
                      <li key={idx} className="text-sm text-purple-800">{point}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Latest Version Content */}
              {document.versions && document.versions.length > 0 && (
                <div>
                  {document.versions[document.versions.length - 1].content ? (
                    <div className="prose max-w-none">
                      <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                        <pre className="whitespace-pre-wrap text-sm text-gray-800">
                          {document.versions[document.versions.length - 1].content}
                        </pre>
                      </div>
                      {/* Download as PDF button for browser-created documents */}
                      <div className="mt-4 text-center">
                        <Button
                          onClick={() => handleDownloadVersion(document.current_version)}
                          variant="outline"
                          className="border-blue-600 text-blue-600 hover:bg-blue-50"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download as PDF
                        </Button>
                      </div>
                    </div>
                  ) : document.versions[document.versions.length - 1].file_path ? (
                    <div className="text-center py-8">
                      <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 mb-4">File-based document</p>
                      <Button
                        onClick={() => handleDownloadVersion(document.current_version)}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download Current Version
                      </Button>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">No content available</p>
                  )}
                </div>
              )}
            </div>

            {/* Version History */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Version History</h2>
              <div className="space-y-3">
                {document.versions?.map((version, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="font-semibold text-gray-900">{version.version_number}</span>
                          <span className={`px-2 py-0.5 rounded text-xs ${getStatusBadge(version.status)}`}>
                            {version.status}
                          </span>
                          <span className="text-xs text-gray-500">
                            ({version.version_type})
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{version.change_description || 'No description'}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          by {version.created_by_name} on {new Date(version.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      {version.file_path && (
                        <Button
                          onClick={() => handleDownloadVersion(version.version_number)}
                          variant="outline"
                          size="sm"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Approval Workflow */}
            {document.approval_workflow && document.approval_workflow.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Approval Workflow</h2>
                <div className="space-y-3">
                  {document.approval_workflow.map((approval, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center space-x-2">
                            <User className="w-4 h-4 text-gray-500" />
                            <span className="font-semibold text-gray-900">{approval.approver_name}</span>
                            <span className="text-sm text-gray-500">({approval.approver_role})</span>
                          </div>
                          {approval.comments && (
                            <p className="text-sm text-gray-600 mt-2">{approval.comments}</p>
                          )}
                          {approval.action_date && (
                            <p className="text-xs text-gray-500 mt-1">
                              {new Date(approval.action_date).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${
                          approval.status === 'Approved' ? 'bg-green-100 text-green-700' :
                          approval.status === 'Rejected' ? 'bg-red-100 text-red-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {approval.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Actions */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Actions</h3>

              {/* Submit for Review */}
              {document.current_status === 'Draft' && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Submit for Review
                  </label>
                  
                  {/* Searchable Reviewer Dropdown */}
                  <div className="relative" ref={reviewerDropdownRef}>
                    <div
                      onClick={() => setShowReviewerDropdown(!showReviewerDropdown)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white cursor-pointer flex items-center justify-between"
                    >
                      <span className={selectedReviewerName ? 'text-gray-900' : 'text-gray-500'}>
                        {selectedReviewerName || 'Select Reviewer'}
                      </span>
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showReviewerDropdown ? 'rotate-180' : ''}`} />
                    </div>
                    
                    {showReviewerDropdown && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-hidden">
                        {/* Search Input */}
                        <div className="p-2 border-b border-gray-200">
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="text"
                              placeholder="Search reviewers..."
                              value={reviewerSearch}
                              onChange={(e) => setReviewerSearch(e.target.value)}
                              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </div>
                        
                        {/* Reviewer List */}
                        <div className="max-h-48 overflow-y-auto">
                          {users
                            .filter(u => (u.role === 'Safety Manager' || u.role === 'Admin') && 
                                        u.name.toLowerCase().includes(reviewerSearch.toLowerCase()))
                            .map(user => (
                              <div
                                key={user.id}
                                onClick={() => {
                                  setReviewerId(user.id);
                                  setSelectedReviewerName(user.name);
                                  setShowReviewerDropdown(false);
                                  setReviewerSearch('');
                                }}
                                className={`px-4 py-2 cursor-pointer hover:bg-blue-50 transition-colors ${
                                  reviewerId === user.id ? 'bg-blue-100' : ''
                                }`}
                              >
                                <div className="flex items-center space-x-2">
                                  <User className="w-4 h-4 text-gray-400" />
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">{user.name}</p>
                                    <p className="text-xs text-gray-500">{user.role}</p>
                                  </div>
                                </div>
                              </div>
                            ))
                          }
                          {users.filter(u => (u.role === 'Safety Manager' || u.role === 'Admin') && 
                                            u.name.toLowerCase().includes(reviewerSearch.toLowerCase())).length === 0 && (
                            <div className="px-4 py-8 text-center text-gray-500 text-sm">
                              No reviewers found
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <Button
                    onClick={handleSubmitForReview}
                    disabled={!reviewerId || actionLoading}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    {actionLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Submit for Review
                  </Button>
                </div>
              )}

              {/* Review Actions */}
              {canReview() && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Review Comments
                  </label>
                  <textarea
                    value={reviewComments}
                    onChange={(e) => setReviewComments(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter your review comments..."
                  />
                  <div className="flex space-x-2">
                    <Button
                      onClick={() => handleReview('approve')}
                      disabled={actionLoading}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                    >
                      {actionLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <ThumbsUp className="w-4 h-4 mr-2" />
                      )}
                      Approve
                    </Button>
                    <Button
                      onClick={() => handleReview('reject')}
                      disabled={actionLoading}
                      className="flex-1 bg-red-600 hover:bg-red-700"
                    >
                      {actionLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <ThumbsDown className="w-4 h-4 mr-2" />
                      )}
                      Reject
                    </Button>
                  </div>
                </div>
              )}

              {/* Publish Action */}
              {document.current_status === 'Approved' && (
                <Button
                  onClick={handlePublish}
                  disabled={actionLoading}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  {actionLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Publish Document
                </Button>
              )}
            </div>

            {/* Acknowledgment Tracking */}
            {document.mandatory_reading && acknowledgments && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Acknowledgment Tracking</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                    <span className="text-sm text-gray-700">Completion Rate</span>
                    <span className="font-bold text-blue-700">{acknowledgments.completion_rate}%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Acknowledged</span>
                    <span className="font-semibold text-gray-900">
                      {acknowledgments.total_acknowledged} / {acknowledgments.total_target}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all"
                      style={{ width: `${acknowledgments.completion_rate}%` }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Keywords */}
            {document.keywords && document.keywords.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Keywords</h3>
                <div className="flex flex-wrap gap-2">
                  {document.keywords.map((keyword, idx) => (
                    <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Applicable Roles */}
            {document.applicable_roles && document.applicable_roles.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Applicable Roles</h3>
                <div className="space-y-2">
                  {document.applicable_roles.map((role, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-700">{role}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
          </>
        ) : (
          /* Audit History Tab */
          <Card>
            <CardContent className="pt-6">
              <AuditTrailViewer
                entityType="document"
                entityId={id}
                title="Document Audit History"
              />
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default DocumentDetail;
